import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PaymentTransaction {
  id: string;
  orderId: string;
  orderNumber?: string;
  amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
  paymentMethod?: string;
  createdAt: string;
  updatedAt: string;
  riskScore?: number;
  webhooks?: Array<{
    webhook_type: string;
    signature_verified: boolean;
    processed_at: string;
    processing_status: string;
  }>;
}

interface PaymentStatusProps {
  transactionId: string;
  autoRefresh?: boolean;
  showAdminDetails?: boolean;
}

export const PaymentStatus: React.FC<PaymentStatusProps> = ({
  transactionId,
  autoRefresh = false,
  showAdminDetails = false
}) => {
  const [transaction, setTransaction] = useState<PaymentTransaction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPaymentStatus = async () => {
    try {
      setError(null);

      const { data, error } = await supabase.functions.invoke('get-payment-status', {
        body: { transactionId }
      });

      if (error) {
        throw new Error(error.message || 'שגיאה בטעינת סטטוס התשלום');
      }

      if (!data.success) {
        throw new Error(data.error || 'שגיאה בטעינת סטטוס התשלום');
      }

      setTransaction(data.transaction);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'שגיאה לא צפויה';
      setError(errorMessage);

      toast.error("שגיאה בטעינת סטטוס התשלום", {
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentStatus();

    if (autoRefresh) {
      const interval = setInterval(fetchPaymentStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [transactionId, autoRefresh]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'processing':
        return <Loader2 className="h-5 w-5 text-primary animate-spin" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-warning" />;
      case 'refunded':
        return <RefreshCw className="h-5 w-5 text-info" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'הושלם';
      case 'failed':
        return 'נכשל';
      case 'processing':
        return 'מעובד';
      case 'pending':
        return 'ממתין';
      case 'refunded':
        return 'הוחזר';
      default:
        return 'לא ידוע';
    }
  };

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'failed':
        return 'destructive';
      case 'processing':
        return 'secondary';
      case 'pending':
        return 'outline';
      case 'refunded':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center p-6">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>טוען סטטוס תשלום...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardContent className="p-6">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button
            onClick={fetchPaymentStatus}
            className="w-full mt-4"
            variant="outline"
          >
            נסה שוב
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!transaction) {
    return (
      <Card className="w-full">
        <CardContent className="p-6">
          <Alert>
            <AlertDescription>לא נמצא מידע על התשלום</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {getStatusIcon(transaction.status)}
          סטטוס תשלום
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-muted-foreground">מזהה עסקה:</span>
            <p className="font-mono text-sm">{transaction.id}</p>
          </div>
          <div>
            <span className="text-sm text-muted-foreground">סטטוס:</span>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={getStatusVariant(transaction.status)}>
                {getStatusText(transaction.status)}
              </Badge>
            </div>
          </div>
          {transaction.orderNumber && (
            <div>
              <span className="text-sm text-muted-foreground">מספר הזמנה:</span>
              <p className="font-mono text-sm">{transaction.orderNumber}</p>
            </div>
          )}
          <div>
            <span className="text-sm text-muted-foreground">סכום:</span>
            <p className="font-bold">{formatAmount(transaction.amount, transaction.currency)}</p>
          </div>
          <div>
            <span className="text-sm text-muted-foreground">תאריך יצירה:</span>
            <p className="text-sm">{formatDate(transaction.createdAt)}</p>
          </div>
          <div>
            <span className="text-sm text-muted-foreground">עדכון אחרון:</span>
            <p className="text-sm">{formatDate(transaction.updatedAt)}</p>
          </div>
        </div>

        {transaction.paymentMethod && (
          <div>
            <span className="text-sm text-muted-foreground">אמצעי תשלום:</span>
            <p className="text-sm">{transaction.paymentMethod}</p>
          </div>
        )}

        {showAdminDetails && transaction.riskScore !== undefined && (
          <div className="border-t pt-4">
            <h4 className="font-medium mb-2">פרטי מנהל</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">ציון סיכון:</span>
                <p className={`font-mono ${transaction.riskScore > 50 ? 'text-destructive' : transaction.riskScore > 20 ? 'text-warning' : 'text-success'}`}>
                  {transaction.riskScore}
                </p>
              </div>
            </div>

            {transaction.webhooks && transaction.webhooks.length > 0 && (
              <div className="mt-4">
                <h5 className="font-medium mb-2">Webhooks אחרונים</h5>
                <div className="space-y-2">
                  {transaction.webhooks.map((webhook, index) => (
                    <div key={index} className="text-xs border rounded p-2">
                      <div className="flex justify-between">
                        <span>{webhook.webhook_type}</span>
                        <Badge variant={webhook.signature_verified ? "default" : "destructive"} className="text-xs">
                          {webhook.signature_verified ? 'מאומת' : 'לא מאומת'}
                        </Badge>
                      </div>
                      <div className="text-muted-foreground">
                        {formatDate(webhook.processed_at)} - {webhook.processing_status}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <Button
          onClick={fetchPaymentStatus}
          variant="outline"
          className="w-full"
          disabled={loading}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          רענן סטטוס
        </Button>
      </CardContent>
    </Card>
  );
};